/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { Formik, type FormikHelpers, type FormikProps } from 'formik'
import { isEqual } from 'lodash'
import { useMemo, type FC } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  id: yup.string(),
  postId: yup.string().nullable(),
  url: yup.string().url().nullable().matches(
    /^(https?:\/\/)?(www\.)?(instagram\.com|instagr\.am)\/p\/[a-zA-Z0-9_-]+\/embed$/,
    'URL inválida, o padrão é https://www.instagram.com/p/DG-r_iFPPWA/embed'
  ),
  imageUrl: yup.string().url().nullable(),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('active'),
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormInstagramPostHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormInstagramPostHandleSubmit,
  initialValues?: Partial<ValidationSchema>
}

const FormInstagramPost: FC<FormikProps<ValidationSchema>> = ({
  handleSubmit,
  handleChange,
  handleBlur,
  setFieldValue,
  isSubmitting,
  isValid,
  values,
  errors,
  initialValues = {},
}) => {
  const navigate = useNavigate()

  const isChanged: boolean = useMemo(() => {
    return !isEqual(values, initialValues)
  }, [values, initialValues])

  return (
    <Row>
      <Col md={8} className='mb-3'>
        <form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>Código</Form.Label>
              <Form.Control
                type='text'
                name='id'
                value={values.id ?? ''}
                placeholder='O id será gerado automaticamente'
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
              />
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>URL da postagem</Form.Label>
              <Form.Control
                type='text'
                placeholder='Ex: https://www.instagram.com/p/DGTFMGIz6b3/embed'
                name='url'
                value={values.url ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
              {errors.url && <p>{errors.url}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>ID da postagem</Form.Label>
              <Form.Control
                type='text'
                placeholder='Será gerado automaticamente'
                name='postId'
                value={values.postId ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
              />
              {errors.postId && <p>{errors.postId}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <Form.Label>URL da imagem da postagem</Form.Label>
              <Form.Control
                type='text'
                placeholder='Será gerado automaticamente'
                name='imageUrl'
                value={values.imageUrl ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
              />
              {errors.imageUrl && <p>{errors.imageUrl}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className='mb-3'>
              <Form.Label>Visibilidade</Form.Label>
              <Form.Select
                value={values.status ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                name='status'
              >
                <option>Selecione</option>
                <option value='active'>🌎 Pública</option>
                <option value='inactive'>🔒 Privada</option>
              </Form.Select>
              {errors.status && <p>{errors.status}</p>}
            </Form.Group>
            <Col md={12}>
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || !isChanged}
              >
                Salvar postagem
              </Button>
              <Button
                variant="secondary"
                style={{ marginLeft: 10 }}
                onClick={() => navigate('/instagram-posts')}
                disabled={isSubmitting}
              >
                Voltar para as postagens
              </Button>
            </Col>
          </Row>
        </form>
      </Col>
      <Col md={4}>
        <small>
          <strong>Sugestão</strong>
          <br />
          Acesse a postagem no aplicativo do Instagram, clique no botão
          {' '}
          <strong>Compartilhar</strong> e em seguida no botão <strong>Copiar link</strong>.
        </small>
      </Col>
    </Row>
  )
}

const FormInstagramPostWrapper: FC<Props> = ({ onSubmit = () => { }, initialValues = {} }) => (
  <Formik
    component={FormInstagramPost}
    validateOnChange
    validateOnBlur
    validateOnMount
    enableReinitialize
    validationSchema={validationSchema}
    initialValues={validationSchema.cast(initialValues)}
    onSubmit={onSubmit}
  />
)

export default FormInstagramPostWrapper
